import React, { useEffect, useState } from 'react';
import { SpaceBetween, Box, Spinner, Badge } from '@cloudscape-design/components';
import { browse } from './browseClient';
import AgentRevealBanner from './AgentRevealBanner';
import { getTheme } from '../themes';

/**
 * One data-driven, themed storefront for ANY of the 8 industry-vertical sellers.
 * Fetches the seller's free /browse/vertical/<id> doc (palette + browsable catalog)
 * and renders the layout it declares (ticker / list / inventory / media / grid /
 * models / datasets / restaurants). Browsing is free (humans browse free); the agent
 * still pays for the priced skills — its activity surfaces via AgentRevealBanner.
 *
 * Adding a new vertical needs NO new component: give it a VERTICAL_STORES entry on
 * the seller and (if its shape is novel) one more `case` in renderRow below.
 */

function Masthead({ s, theme }) {
  // On a dark masthead the brand accent is used directly; on a light masthead it
  // would wash out, so use the brand color but keep it readable.
  const accentOnBg = theme.dark ? theme.accent : theme.accent;
  // A light-background masthead (e.g. Airbnb's white) would blend into the white
  // panel — give it a hairline border + soft accent wash so it still reads as a hero.
  const lightMast = !theme.dark;
  return (
    <div style={{
      background: lightMast ? `linear-gradient(180deg, ${theme.accent}0d, ${theme.surface})` : theme.bg,
      color: theme.ink, borderRadius: theme.radius, padding: '16px 18px',
      fontFamily: theme.headingFont,
      border: lightMast ? `1px solid ${theme.border}` : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 28 }}>{s.emoji}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.3px' }}>
            {s.name}{' '}
            <span style={{ color: accentOnBg, fontWeight: 700, fontSize: 12, opacity: 0.95 }}>· {s.kind}</span>
          </div>
          <div style={{ fontSize: 12, opacity: 0.8, fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>{s.tagline}</div>
        </div>
      </div>
      {/* Brand accent rule under the masthead. */}
      <div style={{ height: 3, marginTop: 12, borderRadius: 2, background: theme.accent }} />
    </div>
  );
}

/** A pay-per-call "row" → the agent buys via x402; humans see the teaser free. */
function PriceTag({ children, accent }) {
  return (
    <span style={{ border: `1px solid ${accent}`, color: accent, borderRadius: 6, padding: '1px 7px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

function Card({ accent, children, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        border: '1px solid #e9ebed', borderLeft: `3px solid ${accent}`, borderRadius: 8,
        padding: '10px 12px', background: '#fff', cursor: onClick ? 'pointer' : 'default',
        display: 'flex', flexDirection: 'column', gap: 4,
      }}
    >
      {children}
    </div>
  );
}

function Grid({ children, min = 150 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 10 }}>
      {children}
    </div>
  );
}

function renderBody(s, theme) {
  const accent = theme.accent;
  const rows = s.rows || [];
  switch (s.layout) {
    case 'ticker':
      return (
        <Grid min={130}>
          {rows.map((r) => {
            const up = r.change >= 0;
            return (
              <Card key={r.arg} accent={up ? '#16a34a' : '#dc2626'}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <b>{r.label}</b>
                  <span style={{ fontSize: 12, color: up ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                    {up ? '▲' : '▼'} {Math.abs(r.changePct)}%
                  </span>
                </div>
                <div style={{ fontSize: 20, fontWeight: 800 }}>${r.price}</div>
                <div style={{ fontSize: 11, color: '#5f6b7a' }}>{up ? '+' : ''}{r.change} today</div>
              </Card>
            );
          })}
        </Grid>
      );

    case 'list': // formulary
      return (
        <SpaceBetween size="xs">
          {rows.map((r) => (
            <Card key={r.arg} accent={accent}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <b>{r.label}</b>
                <SpaceBetween direction="horizontal" size="xxs">
                  <Badge color={r.covered ? 'green' : 'red'}>{r.covered ? 'Covered' : 'Not covered'}</Badge>
                  <Badge color="grey">Tier {r.tier}</Badge>
                  {r.priorAuth && <Badge color="blue">Prior-auth</Badge>}
                </SpaceBetween>
              </div>
            </Card>
          ))}
        </SpaceBetween>
      );

    case 'inventory':
      return (
        <Grid min={170}>
          {rows.map((r) => (
            <Card key={r.arg} accent={r.low ? '#dc2626' : accent}>
              <b style={{ fontSize: 13 }}>{r.label}</b>
              <div style={{ fontSize: 11, color: '#5f6b7a' }}>{r.sku}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 18, fontWeight: 800 }}>{r.onHand}</span>
                {r.low ? <Badge color="red">Reorder</Badge> : <Badge color="green">In stock</Badge>}
              </div>
              <div style={{ fontSize: 10, color: '#9aa3ad' }}>reorder &lt; {r.reorderPoint}</div>
            </Card>
          ))}
        </Grid>
      );

    case 'media':
      return (
        <Grid min={150}>
          {rows.map((r) => (
            <Card key={r.arg} accent={accent}>
              <div style={{ fontSize: 36, textAlign: 'center' }}>{r.art}</div>
              <b style={{ fontSize: 13 }}>{r.label}</b>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Badge color="grey">{r.kind}</Badge>
                <PriceTag accent={accent}>${r.fee} / use</PriceTag>
              </div>
            </Card>
          ))}
        </Grid>
      );

    case 'grid': // energy
      return (
        <SpaceBetween size="s">
          <Box variant="awsui-key-label">Surplus nodes</Box>
          <Grid min={150}>
            {rows.map((r) => (
              <Card key={r.arg} accent={accent}>
                <b style={{ fontSize: 13 }}>{r.label}</b>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{r.kwh} <span style={{ fontSize: 12 }}>kWh</span></div>
                <PriceTag accent={accent}>${r.pricePerKwh}/kWh</PriceTag>
              </Card>
            ))}
          </Grid>
          <Box variant="awsui-key-label">Regional demand</Box>
          <Grid min={150}>
            {(s.regions || []).map((rg) => (
              <Card key={rg.region} accent={theme.accent2}>
                <b style={{ fontSize: 13 }}>{rg.region}</b>
                <div style={{ fontSize: 16, fontWeight: 800 }}>{rg.demandMw.toLocaleString()} MW</div>
                <div style={{ fontSize: 11, color: '#16a34a' }}>{rg.renewablesPct}% renewable</div>
              </Card>
            ))}
          </Grid>
        </SpaceBetween>
      );

    case 'models':
      return (
        <Grid min={180}>
          {rows.map((r) => (
            <Card key={r.arg} accent={accent}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <b style={{ fontFamily: 'monospace', fontSize: 13 }}>{r.id}</b>
                <PriceTag accent={accent}>${r.price}/call</PriceTag>
              </div>
              <div style={{ fontSize: 12, color: '#5f6b7a' }}>{r.task}</div>
              <div style={{ fontSize: 10, color: '#9aa3ad' }}>~{r.latency} latency</div>
            </Card>
          ))}
        </Grid>
      );

    case 'datasets':
      return (
        <Grid min={190}>
          {rows.map((r) => (
            <Card key={r.arg} accent={accent}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <b style={{ fontSize: 13 }}>{r.title}</b>
                <Badge color="grey">{r.cat}</Badge>
              </div>
              <div style={{ fontSize: 11, color: '#5f6b7a' }}>{r.rows} rows · {r.slug}</div>
            </Card>
          ))}
        </Grid>
      );

    case 'restaurants':
      return (
        <Grid min={170}>
          {rows.map((r) => (
            <Card key={r.name} accent={accent}>
              <div style={{ fontSize: 30, textAlign: 'center' }}>{r.art}</div>
              <b style={{ fontSize: 13 }}>{r.name}</b>
              <div style={{ fontSize: 11, color: '#5f6b7a' }}>{r.cuisine} · ⭐ {r.rating}</div>
              <Badge color="green">{r.availability}</Badge>
            </Card>
          ))}
        </Grid>
      );

    default:
      return <Box color="text-body-secondary">No catalog.</Box>;
  }
}

export default function VerticalStore({ sellerId, reveals = [] }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    browse.vertical(sellerId)
      .then((d) => alive && setS(d))
      .catch(() => alive && setS({ error: true }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [sellerId]);

  if (loading) return <Spinner />;
  if (!s || s.error) return <Box color="text-status-error">Storefront unavailable.</Box>;

  const theme = getTheme(sellerId);

  return (
    <SpaceBetween size="m">
      <Masthead s={s} theme={theme} />
      <AgentRevealBanner reveals={reveals} accent={theme.accent} />
      {renderBody(s, theme)}
      <Box fontSize="body-s" color="text-body-secondary" textAlign="center">
        Browsing is free. The agent pays per call via WAF Monetization to pull live {s.kind.toLowerCase()} data.
      </Box>
    </SpaceBetween>
  );
}
